import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CRISIS_RUNBOOK_DEFAULT } from '@lessalone/shared';
import { ModerationNav } from '../components/ModerationNav';
import {
  fetchModerationEventDetail,
  resolveEvent,
  type MeResponse,
  type ModerationEventDetail,
  type ModeratorAction,
} from '../lib/api';
import { confirmDialog } from '../lib/dialog';

function label(value: string): string {
  return value.replace(/_/g, ' ');
}

interface OpusReviewBlock {
  decision: 'dismiss' | 'uphold' | 'warn' | 'suspend' | 'escalate';
  effective_decision: 'dismiss' | 'uphold' | 'warn' | 'suspend' | 'escalate';
  confidence: number;
  reasoning: string;
  category_correction?: string | null;
  reviewed_at: string;
  model: string;
}

/**
 * Full-context view of one moderation event — the subject's identity, the
 * flagged message body, the trailing 20 messages of the conversation, and
 * the audit-log entries that reference the event. Action buttons mirror
 * ReviewQueue.
 */
export function ModerationEvent({ me }: { me: MeResponse }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const communityId = me.memberships[0]?.communityId ?? '';
  const [detail, setDetail] = useState<ModerationEventDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    if (!communityId || !id) return;
    fetchModerationEventDetail(communityId, id)
      .then((d) => {
        if (d) setDetail(d);
        else setNotFound(true);
      })
      .catch(() => setError('Could not load the event.'));
  }, [communityId, id]);

  useEffect(load, [load]);

  async function act(action: ModeratorAction, confirmMessage?: string) {
    if (confirmMessage) {
      const [title, ...rest] = confirmMessage.split('\n\n');
      const message = rest.join('\n\n');
      const isBan = action === 'ban_user';
      const ok = await confirmDialog({
        title: title ?? 'Are you sure?',
        message: message || undefined,
        confirmLabel: isBan ? 'Ban this member' : 'Suspend',
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    const result = await resolveEvent(communityId, id, action);
    setBusy(false);
    if (result.ok) navigate('/moderation');
    else setError(result.error);
  }

  if (notFound) {
    return (
      <main className="wide">
        <div className="card">
          <ModerationNav />
          <p>Event not found.</p>
          <p>
            <Link to="/moderation">← Back to the queue</Link>
          </p>
        </div>
      </main>
    );
  }

  if (!detail) {
    return (
      <main className="wide">
        <div className="card">
          <ModerationNav />
          <p>Loading…</p>
          {error && <p className="error">{error}</p>}
        </div>
      </main>
    );
  }

  const isCrisis = detail.event.category === 'self_harm';

  return (
    <main className="wide">
      <div className="card">
        <ModerationNav />
        <p className="step-label">
          <Link to="/moderation">← Review queue</Link>
        </p>

        <div className={`event sev-${detail.event.severity}`}>
          <div className="event-head">
            <span className={`badge sev-${detail.event.severity}`}>{detail.event.severity}</span>
            <strong>{label(detail.event.category)}</strong>
            <span className="muted">· {label(detail.event.source)}</span>
          </div>
          {detail.message && <blockquote>{detail.message.body}</blockquote>}
          <p className="muted">
            {new Date(detail.event.createdAt).toLocaleString()}
            {detail.event.confidence !== null && <> · confidence {detail.event.confidence}</>}
          </p>

          {detail.subject && (
            <p>
              <strong>Subject:</strong>{' '}
              <Link to={`/moderation/users/${detail.subject.userId}`}>
                {detail.subject.nickname}
              </Link>{' '}
              (real name {detail.subject.name}, {detail.subject.email}, account{' '}
              {detail.subject.status})
            </p>
          )}

          {(() => {
            // Surface Opus's reasoning when the second-pass reviewer
            // has weighed in. Rendered identically for actioned and
            // open-with-escalation cases — the moderator sees what
            // Opus thought either way, and on escalations the call to
            // act sits below.
            const d = detail.event.detail as { opus?: OpusReviewBlock } | null;
            const opus = d?.opus;
            if (!opus) return null;
            const isEscalation = opus.effective_decision === 'escalate';
            return (
              <aside className={`opus-block${isEscalation ? ' opus-escalated' : ''}`}>
                <div className="opus-block-header">
                  <strong>
                    Opus 4.7 · <span className="opus-decision">{opus.effective_decision}</span>
                  </strong>
                  <span className="muted">
                    confidence {(opus.confidence * 100).toFixed(0)}%
                    {opus.decision !== opus.effective_decision &&
                      ` · recommended ${opus.decision} (below floor → escalated)`}
                  </span>
                </div>
                <p className="opus-reasoning">{opus.reasoning}</p>
                {opus.category_correction && (
                  <p className="muted opus-note">
                    Category correction: {label(opus.category_correction)}
                  </p>
                )}
              </aside>
            );
          })()}

          {isCrisis && (
            <aside className="runbook">
              <div className="runbook-header">
                <strong>Run-book — what to do next</strong>
                <span className="muted">
                  Escalation: {detail.community.crisisEscalationName} (
                  {detail.community.crisisEscalationContact},{' '}
                  {label(detail.community.crisisEscalationType)})
                </span>
              </div>
              <pre className="runbook-body">
                {detail.community.crisisRunbook ?? CRISIS_RUNBOOK_DEFAULT}
              </pre>
              {detail.community.crisisResourceNote && (
                <p className="muted runbook-note">{detail.community.crisisResourceNote}</p>
              )}
            </aside>
          )}

          {detail.event.status === 'open' &&
            (() => {
              // Mirror the queue treatment: stacked tiles with verb +
              // consequence hint + Opus's recommended action highlighted
              // (gold ring + ★ Opus pick badge). Opus reasoning was
              // already rendered above; this adds the visual pointer.
              const d = detail.event.detail as { opus?: OpusReviewBlock } | null;
              const opus = d?.opus;
              const opusAction: ModeratorAction | null = opus
                ? opus.effective_decision === 'dismiss'
                  ? 'dismiss'
                  : opus.effective_decision === 'uphold' || opus.effective_decision === 'warn'
                    ? 'uphold'
                    : opus.effective_decision === 'suspend'
                      ? 'suspend_user'
                      : null
                : null;
              return (
                <div className="event-actions">
                  {isCrisis ? (
                    <>
                      <button
                        className="mod-action mod-action--escalate"
                        disabled={busy}
                        onClick={() => void act('escalate_crisis')}
                      >
                        <span className="mod-action-verb">Mark escalated</span>
                        <span className="mod-action-hint">
                          You contacted the community's crisis endpoint.
                        </span>
                      </button>
                      <button
                        className="mod-action mod-action--dismiss"
                        disabled={busy}
                        onClick={() => void act('dismiss')}
                      >
                        <span className="mod-action-verb">Dismiss</span>
                        <span className="mod-action-hint">Close the event without acting.</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="mod-action mod-action--dismiss"
                        data-recommended={opusAction === 'dismiss' ? 'true' : undefined}
                        disabled={busy}
                        onClick={() => void act('dismiss')}
                      >
                        <span className="mod-action-verb">Dismiss</span>
                        <span className="mod-action-hint">
                          False positive. Message stays as-is, account untouched.
                        </span>
                        {opusAction === 'dismiss' && (
                          <span className="mod-action-rec">★ Opus pick</span>
                        )}
                      </button>
                      <button
                        className="mod-action mod-action--uphold"
                        data-recommended={opusAction === 'uphold' ? 'true' : undefined}
                        disabled={busy}
                        onClick={() => void act('uphold')}
                      >
                        <span className="mod-action-verb">Uphold</span>
                        <span className="mod-action-hint">
                          Flag is valid. Message stays hidden; no account action.
                        </span>
                        {opusAction === 'uphold' && (
                          <span className="mod-action-rec">★ Opus pick</span>
                        )}
                      </button>
                      <button
                        className="mod-action mod-action--suspend"
                        data-recommended={opusAction === 'suspend_user' ? 'true' : undefined}
                        disabled={busy}
                        onClick={() =>
                          void act(
                            'suspend_user',
                            'Suspend this member?\n\nThey will not be able to sign in or be matched until reinstated. Active conversations end immediately.',
                          )
                        }
                      >
                        <span className="mod-action-verb">Suspend</span>
                        <span className="mod-action-hint">
                          Lock them out, end active chats. Reversible.
                        </span>
                        {opusAction === 'suspend_user' && (
                          <span className="mod-action-rec">★ Opus pick</span>
                        )}
                      </button>
                      <button
                        className="mod-action mod-action--ban"
                        disabled={busy}
                        onClick={() =>
                          void act(
                            'ban_user',
                            'Ban this member?\n\nPermanent removal. All active conversations end. They cannot be matched again. This is the most severe action — use for repeat offenders or sexual / harassment violations with clear intent.',
                          )
                        }
                      >
                        <span className="mod-action-verb">Ban</span>
                        <span className="mod-action-hint">
                          Permanent removal. End every conversation. Use sparingly.
                        </span>
                      </button>
                    </>
                  )}
                </div>
              );
            })()}
          {detail.event.status !== 'open' && (
            <p className="muted">
              Already resolved{' '}
              {detail.event.reviewedAt && (
                <>at {new Date(detail.event.reviewedAt).toLocaleString()}</>
              )}
              : {detail.event.resolution}
            </p>
          )}
          {error && <p className="error">{error}</p>}
        </div>

        {detail.conversationContext && (
          <section>
            <h2>Conversation context</h2>
            <p className="muted">Up to the last 20 messages in this conversation.</p>
            <div className="messages">
              {detail.conversationContext.messages.map((m) => (
                <div key={m.id} className="msg peer">
                  <strong>{m.senderNickname}</strong>
                  <br />
                  {m.body}
                  <span className="msg-time">
                    {new Date(m.createdAt).toLocaleString()}
                    {!m.visibleToPeer && ' · hidden from peer'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {detail.auditEntries.length > 0 && (
          <section>
            <h2>Audit entries</h2>
            <ul className="audit">
              {detail.auditEntries.map((a) => (
                <li key={a.id}>
                  <code>{a.action}</code> on {a.targetType} {a.targetId} at{' '}
                  {new Date(a.createdAt).toLocaleString()}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}
